import { useEffect, useMemo, useRef, useState } from 'react'

export type UseTypewriterTextOptions = {
  enabled?: boolean
  delayMs?: number
  charIntervalMs?: number
  stopWhen?: boolean
  loop?: boolean
  backspaceIntervalMs?: number
  pauseAfterTypeMs?: number
  pauseAfterDeleteMs?: number
}

export function useTypewriterText(
  target: string | string[],
  opts?: UseTypewriterTextOptions,
): string {
  const options = {
    enabled: opts?.enabled ?? false,
    delayMs: opts?.delayMs ?? 0,
    charIntervalMs: opts?.charIntervalMs ?? 24,
    stopWhen: opts?.stopWhen ?? false,
    loop: opts?.loop ?? true,
    backspaceIntervalMs: opts?.backspaceIntervalMs ?? 18,
    pauseAfterTypeMs: opts?.pauseAfterTypeMs ?? 1200,
    pauseAfterDeleteMs: opts?.pauseAfterDeleteMs ?? 600,
  }

  const [rendered, setRendered] = useState<string>('')
  const intervalRef = useRef<number | null>(null)
  const timeoutRef = useRef<number | null>(null)
  const phaseRef = useRef<'idle' | 'typing' | 'deleting'>('idle')
  const currentIndexRef = useRef<number>(0)

  // Normalize target to array
  const targetList = useMemo(
    () => (Array.isArray(target) ? target : [target]),
    [target],
  )

  useEffect(() => {
    function clearAll() {
      if (intervalRef.current) {
        window.clearInterval(intervalRef.current)
        intervalRef.current = null
      }
      if (timeoutRef.current) {
        window.clearTimeout(timeoutRef.current)
        timeoutRef.current = null
      }
    }

    if (
      !options.enabled ||
      options.stopWhen ||
      !targetList.length ||
      targetList.every(t => !t)
    ) {
      setRendered('')
      clearAll()
      phaseRef.current = 'idle'
      currentIndexRef.current = 0
      return
    }

    function scheduleTyping() {
      phaseRef.current = 'typing'
      const currentText = targetList[currentIndexRef.current]
      let i = 0
      intervalRef.current = window.setInterval(() => {
        i += 1
        setRendered(currentText.slice(0, i))
        if (i >= currentText.length) {
          clearAll()
          if (options.loop) {
            timeoutRef.current = window.setTimeout(
              scheduleDeleting,
              options.pauseAfterTypeMs,
            )
          }
        }
      }, options.charIntervalMs)
    }

    function scheduleDeleting() {
      phaseRef.current = 'deleting'
      const currentText = targetList[currentIndexRef.current]
      let i = currentText.length
      intervalRef.current = window.setInterval(() => {
        i -= 1
        setRendered(currentText.slice(0, Math.max(0, i)))
        if (i <= 0) {
          clearAll()
          if (options.loop) {
            // Move to next prompt in list
            currentIndexRef.current =
              (currentIndexRef.current + 1) % targetList.length
            timeoutRef.current = window.setTimeout(
              scheduleTyping,
              options.pauseAfterDeleteMs,
            )
          }
        }
      }, options.backspaceIntervalMs)
    }

    setRendered('')
    timeoutRef.current = window.setTimeout(
      () => {
        scheduleTyping()
      },
      Math.max(0, options.delayMs),
    )

    return () => {
      clearAll()
      phaseRef.current = 'idle'
      currentIndexRef.current = 0
    }
  }, [
    targetList,
    options.enabled,
    options.stopWhen,
    options.delayMs,
    options.charIntervalMs,
    options.loop,
    options.backspaceIntervalMs,
    options.pauseAfterTypeMs,
    options.pauseAfterDeleteMs,
  ])

  return rendered
}
